import {
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

/**
 * A small right-click menu at the cursor: children are its buttons
 * (`MenuItem`). It opens focused on the first button, moves between them with
 * the arrow keys, closes on Esc (focus returns to what had it), a click
 * outside, scrolling or a resize, and is kept inside the viewport. Drawn in a
 * portal so a panel's transform can't offset it.
 */
export function ContextMenu({
  x,
  y,
  label,
  onClose,
  children,
}: {
  x: number;
  y: number;
  /** Accessible name of the menu. */
  label: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x, top: y });
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  // Clamp to the viewport; runs again when the content grows (a submenu opens).
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const left = Math.max(4, Math.min(x, window.innerWidth - el.offsetWidth - 4));
    const top = Math.max(4, Math.min(y, window.innerHeight - el.offsetHeight - 4));
    setPos((p) => (p.left === left && p.top === top ? p : { left, top }));
  });

  useEffect(() => {
    const opener = document.activeElement;
    ref.current?.querySelector<HTMLElement>('button:not(:disabled)')?.focus();
    const close = () => closeRef.current();
    const outside = (e: Event) => {
      if (!ref.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('pointerdown', outside, true);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('pointerdown', outside, true);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    };
  }, []);

  function onKeyDown(e: KeyboardEvent) {
    // Keys belong to the menu: the match's hotkeys must not also fire.
    e.stopPropagation();
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === 'Tab') {
      onClose();
      return;
    }
    const step = e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : 0;
    if (step === 0 || !ref.current) return;
    e.preventDefault();
    const items = [...ref.current.querySelectorAll<HTMLElement>('button:not(:disabled)')];
    const at = items.indexOf(document.activeElement as HTMLElement);
    items[(at + step + items.length) % items.length]?.focus();
  }

  return createPortal(
    <div
      ref={ref}
      role="menu"
      aria-label={label}
      onKeyDown={onKeyDown}
      onContextMenu={(e) => e.preventDefault()}
      style={pos}
      className="panel pointer-events-auto fixed z-50 w-44 rounded-xl bg-surface-solid p-1 text-xs"
    >
      {children}
    </div>,
    document.body
  );
}

/** One menu button: label left, its shortcut key right. */
export function MenuItem({
  children,
  hint,
  expanded,
  onClick,
}: {
  children: ReactNode;
  hint?: string;
  /** Set for an item that opens more below it. */
  expanded?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      aria-expanded={expanded}
      onClick={onClick}
      className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1 text-left transition hover:bg-fill-hover focus-visible:bg-fill-hover"
    >
      <span>{children}</span>
      {hint && <kbd className="text-[10px] text-fg-subtle">{hint}</kbd>}
    </button>
  );
}
